import { useEffect, useRef, useState } from 'react'
import AlertList from '../alerts/AlertList'
import AlertDetail from '../alerts/AlertDetail'
import HazardFilter from '../filters/HazardFilter'
import SeverityFilter from '../filters/SeverityFilter'
import RegionFilter from '../filters/RegionFilter'
import { useAlertStore } from '../../store/alertStore'
import { useSettingsStore } from '../../store/settingsStore'
import { useAuth } from '../../hooks/useAuth'
import PushNotificationButton from '../PushNotificationButton'

export default function MobileSheet() {
  const [expanded, setExpanded] = useState(false)
  const selectedId = useAlertStore((s) => s.selectedAlertId)
  const alerts = useAlertStore((s) => s.alerts)
  const { theme, toggleTheme } = useSettingsStore()
  const { logout } = useAuth()

  // Auto-expand when an alert is selected from the map
  useEffect(() => {
    if (selectedId) setExpanded(true)
  }, [selectedId])

  // Swipe gesture: track pointer start position, commit on release
  const dragStartY = useRef<number | null>(null)

  const handlePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    dragStartY.current = e.clientY
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const handlePointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (dragStartY.current === null) return
    const delta = dragStartY.current - e.clientY
    dragStartY.current = null

    if (delta > 50) {
      setExpanded(true)
    } else if (delta < -50) {
      setExpanded(false)
    } else {
      // Small movement or tap — treat as toggle click
      setExpanded((v) => !v)
    }
  }

  const handlePointerCancel = () => {
    dragStartY.current = null
  }

  return (
    <div
      className={`absolute bottom-0 left-0 right-0 bg-gray-50 dark:bg-gray-950 border-t border-gray-200 dark:border-gray-800 rounded-t-2xl transition-all duration-300 z-10 flex flex-col overflow-hidden ${
        expanded ? 'h-[70vh]' : 'h-36'
      }`}
      // Extend the sheet's own background into the safe area below the home indicator
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {/* Drag handle — handles both swipe and tap */}
      <div className="flex items-center px-3 shrink-0">
        <button
          type="button"
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
          className="flex flex-col items-center py-2.5 gap-1 flex-1 touch-none"
          aria-label={expanded ? 'Collapse' : 'Expand alert list'}
        >
          <div className="w-10 h-1 bg-gray-300 dark:bg-gray-700 rounded-full" />
          <span className="text-xs text-gray-500">
            {alerts.length} alert{alerts.length !== 1 ? 's' : ''} {expanded ? '▾' : '▸'}
          </span>
        </button>
        <PushNotificationButton />
        <button
          type="button"
          onClick={toggleTheme}
          title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-600 dark:hover:text-gray-300 transition-colors p-1"
        >
          {theme === 'dark' ? (
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
              <circle cx="12" cy="12" r="4"/><path strokeLinecap="round" d="M12 2v2M12 20v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M2 12h2M20 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
            </svg>
          ) : (
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/>
            </svg>
          )}
        </button>
        <button
          type="button"
          onClick={logout}
          title="Sign out"
          className="text-gray-500 hover:text-gray-700 dark:text-gray-600 dark:hover:text-gray-300 transition-colors p-1"
        >
          {/* exit door icon */}
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
        </button>
      </div>

      {expanded && (
        <>
          <div className="border-b border-gray-200 dark:border-gray-800 shrink-0">
            <RegionFilter />
            <HazardFilter />
            <SeverityFilter />
          </div>
          <div className="flex-1 overflow-hidden">
            {selectedId ? <AlertDetail /> : <AlertList />}
          </div>
        </>
      )}

      {!expanded && (
        <div className="px-3 overflow-hidden">
          <HazardFilter />
        </div>
      )}
    </div>
  )
}
